import { injectable } from 'inversify';
import { ApiResponse } from '../utils/api-response.utils';
import { ERRORS } from '../constants/errors';
import { country as countryConstants } from '../constants/country.constants';
import axiosClient from './axios-client';
import { DigitalShelfSubscriptionDetails } from '../_api/repositories';
import { MetricsResponse } from '../models/digital-shelf/product-health.model';
import { RatingReviewsMetricsResponse } from '../models/digital-shelf/product-health.model';
import { PriceCompetitivenessMetricsResponse } from '../models/digital-shelf/product-health.model';
import { ContentQualityMetricsResponse } from '../models/digital-shelf/product-health.model';
import { AvgDiscountDepthTrendResponse } from '../models/digital-shelf/product-health.model';
import { AvgDiscountDepthByCategoryResponse } from '../models/digital-shelf/product-health.model';
import { BrandOptionsResponse } from '../models/digital-shelf/product-health.model';
import {
  DsAlertModule,
  GetDsAlertsParams,
  GetDsAlertsResponse,
  GetDsAlertSummaryResponse,
} from '../models/digital-shelf/ds-alert.model';
import {
  ProductHealthOnlineAvailabilityProductsResult,
  ProductHealthContentQualityProductsResult,
  ProductHealthPriceCompetitivenessProductsResult,
  ProductHealthRatingsAndReviewsProductsResult,
  ProductHealthSelectedProductIdentifier,
  ProductHealthSelectedProductPriceTrendResult,
  SortBy,
  SortOrder,
} from '../_api/models/product-health.model';
import {
  CountryTableMetricsBatchResponse,
  TableMetricsByCategoryResponse,
  TableMetricsByChannelResponse,
  TableMetricsByCountryResponse,
  TrendMetricsByCategoryResponse,
  TrendMetricsByChannelResponse,
  TrendMetricsByCountryResponse,
} from '../models/digital-shelf/global-score-card.model';
import {
  BenchmarkConfigRow,
  BenchmarkModuleTier,
  GetBenchmarkConfigResponse,
  UpsertBenchmarkConfigResponse,
} from '../models/digital-shelf/benchmark-config.model';

import {
  KeywordInsightsDistributionResponse,
  KeywordInsightsMetricsResponse,
  KeywordInsightsPositionTableResponse,
  KeywordInsightsScatterPlotResponse,
  ShareOfSearchKeywordOptionsResponse,
  SearchInsightsMetricsResponse,
  BrandLeaderboardResponse,
  SearchInsightsTopPlayerResponse,
  SearchInsightsRankingCardsResponse,
  SearchInsightsHeadlineCardsResponse,
  CompetitiveGapTrendResponse,
  ResellerOutranksOfficialResponse,
} from '../models/digital-shelf/share-of-search.model';
import {
  BannerPresenceContentInsightResponse,
  BannerPresenceGalleryLogResponse,
  BannerPresenceGalleryLogSortBy,
  BannerPresenceGalleryLogSortOrder,
  BannerPresenceOverviewResponse,
  BannerPresenceQueryParams,
} from '../models/digital-shelf/banner-presence.model';
import {
  PriceTrackingProductFocusQuery,
  PriceTrackingProductOptionsQuery,
  PriceTrackingProductsQuery,
  PriceTrackingTrendQuery,
} from '../models/digital-shelf/price-tracking.model';
import {
  PriceTrackingProductFocusResult,
  PriceTrackingProductOptionsResult,
  PriceTrackingProductsResult,
  PriceTrackingTrendResult,
} from '../_api/models/price-tracking.model';

type ProductHealthOnlineAvailabilityQuery = {
  startDate: string;
  endDate: string;
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  module?: string;
  country?: string;
  channels?: string[];
  categoryPaths?: string[];
  brands?: string[];
  meBrands?: string[];
};

type ProductHealthOnlineAvailabilityProductsQuery = {
  startDate: string;
  endDate: string;
  channels?: string[];
  country?: string;
  brandNames?: string[];
  categoryPaths?: string[];
  isOfficial?: boolean;
  shopNames?: string[];
  searchText?: string;
  limit?: number;
  cursor?: {
    productKey: string;
    sortValue?: string | number;
  };
  sortBy?: SortBy;
  sortOrder?: SortOrder;
};

type ProductHealthRatingReviewsProductsQuery =
  ProductHealthOnlineAvailabilityProductsQuery;
type ProductHealthPriceCompetitivenessProductsQuery =
  ProductHealthOnlineAvailabilityProductsQuery;
type ProductHealthContentQualityProductsQuery =
  ProductHealthOnlineAvailabilityProductsQuery;

type ProductHealthSelectedProductPriceTrendQuery = {
  startDate: string;
  endDate: string;
  country?: string;
  channels?: string[];
  brandNames?: string[];
  categoryPaths?: string[];
  shopNames?: string[];
  product: ProductHealthSelectedProductIdentifier;
};

type GlobalScoreCardTabType = 'Country' | 'Channel' | 'Category';
type GlobalScoreCardTrendGroupBy = 'country' | 'channel' | 'category';

type GlobalScoreCardTableMetricsQuery = {
  startDate: string;
  endDate: string;
  countries?: string[];
  channels?: string[];
  brands?: string[];
  categoryPaths?: string[];
  tab: GlobalScoreCardTabType;
  benchmark?: {
    onlineAvailability?: {
      passPercentageScore?: number;
      failPercentageScore?: number;
    };
    ratings?: {
      passPercentageScore?: number;
      failPercentageScore?: number;
      productRatingBenchmark: number;
    };
    reviews?: {
      passPercentageScore?: number;
      failPercentageScore?: number;
      productReviewBenchmark: number;
    };
    contentQuality?: {
      passPercentageScore?: number;
      failPercentageScore?: number;
      productContentQualityBenchmark: number;
    };
    shareOfSearch?: {
      passPercentageScore?: number;
      failPercentageScore?: number;
    };
  };
};

type GlobalScoreCardCountryTableMetricsBatchQuery = Omit<
  GlobalScoreCardTableMetricsQuery,
  'tab' | 'benchmark'
> & {
  globalBenchmark?: GlobalScoreCardTableMetricsQuery['benchmark'];
  countryBenchmarks?: Record<
    string,
    GlobalScoreCardTableMetricsQuery['benchmark'] | undefined
  >;
};

type GlobalScoreCardTrendMetricsQuery = {
  groupBy: GlobalScoreCardTrendGroupBy;
  startDate: string;
  endDate: string;
  countries?: string[];
  channels?: string[];
  brands?: string[];
  shops?: string[];
  categoryPaths?: string[];
  metricName: string;
  productRatingBenchmark?: number;
  productReviewBenchmark?: number;
  productContentQualityBenchmark?: number;
};

type GlobalScoreCardTableMetricsData =
  | TableMetricsByCountryResponse['data']
  | TableMetricsByChannelResponse['data']
  | TableMetricsByCategoryResponse['data'];

type GlobalScoreCardTrendMetricsData =
  | TrendMetricsByCountryResponse['data']
  | TrendMetricsByChannelResponse['data']
  | TrendMetricsByCategoryResponse['data'];

type ProductHealthRatingReviewsQuery = {
  startDate: string;
  endDate: string;
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  country?: string;
  channels?: string[];
  categoryPaths?: string[];
  brands?: string[];
  meBrands?: string[];
  ratingBenchmark: number;
};

type ProductHealthPriceCompetitivenessQuery = {
  startDate: string;
  endDate: string;
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  country?: string;
  channels?: string[];
  categoryPaths?: string[];
  brands?: string[];
  meBrands?: string[];
  ratingBenchmark: number;
  groupBy?: 'brand' | 'category';
};

type ProductHealthAvgDiscountDepthTrendQuery = {
  startDate: string;
  endDate: string;
  country?: string;
  channels?: string[];
  categoryPaths?: string[];
  brands?: string[];
  meBrands?: string[];
  shopNames?: string[];
  periodType?: 'daily' | 'weekly';
};

type ProductHealthAvgDiscountDepthByCategoryQuery = {
  startDate: string;
  endDate: string;
  country?: string;
  channels?: string[];
  categoryPaths?: string[];
  brands?: string[];
  shopNames?: string[];
  categoryLevel?: 'L2' | 'L3' | 'L4';
};

type ProductHealthContentQualityQuery = {
  startDate: string;
  endDate: string;
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  country?: string;
  channels?: string[];
  categoryPaths?: string[];
  brands?: string[];
  meBrands?: string[];
  contentOverallPassScoreBenchmark?: number;
  contentProductTitleBenchmark?: number;
  contentProductDescriptionBenchmark?: number;
  contentRichMediaBenchmark?: number;
  ratioGroupBy?: 'brand' | 'channel' | 'category';
};

type ShareOfSearchSearchInsightsQuery = {
  startDate: string;
  endDate: string;
  previousStartDate?: string | null;
  previousEndDate?: string | null;
  channels?: string[];
  country?: string;
  categoryPaths?: string[];
  brands?: string[];
  keywords?: string[];
  /** SOS-24: Official fallback toggle -- only read by the headline-cards endpoint. */
  includeMeOfficialOthers?: boolean;
  includeCompetitorOfficialOthers?: boolean;
};

type ShareOfSearchKeywordOptionsQuery = {
  startDate?: string | null;
  endDate?: string | null;
  channels?: string[];
  country?: string;
  categoryPaths?: string[];
};

type CompetitorBrandsQuery = {
  startDate: string | null;
  endDate: string | null;
  country?: string;
  countries?: string[];
  channels?: string[];
  categoryPaths?: string[];
  module: string;
};

type BrandOptionsQuery = {
  country?: string;
  countries?: string[];
  channels?: string[];
  categoryPaths?: string[];
  module: string;
};

type BannerPresenceQuery = Omit<
  BannerPresenceQueryParams,
  'startDate' | 'endDate'
> & {
  startDate: string;
  endDate: string;
  page?: number;
  pageSize?: number;
  searchText?: string;
  sortBy?: BannerPresenceGalleryLogSortBy;
  sortOrder?: BannerPresenceGalleryLogSortOrder;
};

const toCommaSeparated = (values?: string[]): string => {
  if (!values || values.length === 0) return '';
  return values.join(',');
};

const toCategoryRequestParams = ({
  categoryPaths,
}: {
  categoryPaths?: string[];
}) => ({
  categoryPaths: toCommaSeparated(categoryPaths),
});

const COUNTRY_CODE_BY_NAME = new Map(
  countryConstants.data.map((item) => [
    item.name.trim().toLowerCase(),
    item.code,
  ])
);

const toCountryCode = (value?: string | null): string => {
  if (!value) return '';

  const normalizedValue = value.trim();
  if (!normalizedValue) return '';

  const matchedCode = COUNTRY_CODE_BY_NAME.get(normalizedValue.toLowerCase());
  return (matchedCode ?? normalizedValue).toLowerCase();
};

const toCountryCodes = (values?: string[]): string[] =>
  (values ?? []).map((value) => toCountryCode(value)).filter(Boolean);

const toParamValue = (value?: string | null): string => value ?? '';

const buildOnlineAvailabilityParams = (
  params: ProductHealthOnlineAvailabilityQuery
) => ({
  ...params,
  module: toParamValue(params.module),
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  brands: toCommaSeparated(params.brands),
  meBrands: toCommaSeparated(params.meBrands),
});

const buildOnlineAvailabilityProductsParams = (
  params: ProductHealthOnlineAvailabilityProductsQuery
) => ({
  ...params,
  country: toCountryCode(params.country),
  searchText: toParamValue(params.searchText),
  channels: toCommaSeparated(params.channels),
  brandNames: toCommaSeparated(params.brandNames),
  ...toCategoryRequestParams(params),
  shopNames: toCommaSeparated(params.shopNames),
  ...(params.cursor
    ? {
        'cursor.productKey': params.cursor.productKey,
        ...(params.cursor.sortValue !== undefined
          ? { 'cursor.sortValue': params.cursor.sortValue }
          : {}),
      }
    : {}),
  cursor: undefined,
});

const buildRatingReviewsProductsParams = (
  params: ProductHealthRatingReviewsProductsQuery
) => ({
  ...buildOnlineAvailabilityProductsParams(params),
});

const buildPriceCompetitivenessProductsParams = (
  params: ProductHealthPriceCompetitivenessProductsQuery
) => ({
  ...buildOnlineAvailabilityProductsParams(params),
});

const buildSelectedProductPriceTrendParams = (
  params: ProductHealthSelectedProductPriceTrendQuery
) => ({
  ...params,
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  brandNames: toCommaSeparated(params.brandNames),
  ...toCategoryRequestParams(params),
  shopNames: toCommaSeparated(params.shopNames),
  productKey: params.product.productKey,
  productId: params.product.productId,
  skuKey: params.product.skuKey,
  channel: params.product.channel,
  productName: params.product.productName,
  brandName: params.product.brandName,
  product: undefined,
});

const buildContentQualityProductsParams = (
  params: ProductHealthContentQualityProductsQuery
) => ({
  ...buildOnlineAvailabilityProductsParams(params),
});

// No brandNames: Price Tracking has no brand filter.
const buildPriceTrackingBaseParams = (params: {
  country?: string;
  channels?: string[];
  categoryPaths?: string[];
  shopNames?: string[];
}) => ({
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  shopNames: toCommaSeparated(params.shopNames),
});

const buildPriceTrackingProductsParams = (
  params: PriceTrackingProductsQuery
) => ({
  ...params,
  ...buildPriceTrackingBaseParams(params),
  searchText: toParamValue(params.searchText),
  // Flat `cursor.*` keys are the wire format the products routes parse.
  ...(params.cursor
    ? {
        'cursor.productKey': params.cursor.productKey,
        ...(params.cursor.sortValue !== undefined
          ? { 'cursor.sortValue': params.cursor.sortValue }
          : {}),
      }
    : {}),
  cursor: undefined,
});

const buildPriceTrackingTrendParams = (params: PriceTrackingTrendQuery) => ({
  ...params,
  ...buildPriceTrackingBaseParams(params),
  // Stays an array: the route reads `body.products`.
  products: params.products,
});

const buildPriceTrackingProductFocusParams = (
  params: PriceTrackingProductFocusQuery
) => ({
  ...params,
  ...buildPriceTrackingBaseParams(params),
  product: params.product,
});

const buildPriceTrackingProductOptionsParams = (
  params: PriceTrackingProductOptionsQuery
) => ({
  ...params,
  ...buildPriceTrackingBaseParams(params),
  searchText: toParamValue(params.searchText),
});

const buildGlobalScoreCardTrendMetricsParams = (
  params: GlobalScoreCardTrendMetricsQuery
) => ({
  ...params,
  groupBy: toParamValue(params.groupBy),
  countries: toCommaSeparated(toCountryCodes(params.countries)),
  channels: toCommaSeparated(params.channels),
  brands: toCommaSeparated(params.brands),
  shops: toCommaSeparated(params.shops),
  ...toCategoryRequestParams(params),
});

const buildRatingReviewsParams = (params: ProductHealthRatingReviewsQuery) => ({
  ...params,
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  brands: toCommaSeparated(params.brands),
  meBrands: toCommaSeparated(params.meBrands),
});

const buildPriceCompetitivenessParams = (
  params: ProductHealthPriceCompetitivenessQuery
) => ({
  ...params,
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  brands: toCommaSeparated(params.brands),
  meBrands: toCommaSeparated(params.meBrands),
});

const buildAvgDiscountDepthTrendParams = (
  params: ProductHealthAvgDiscountDepthTrendQuery
) => ({
  ...params,
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  brands: toCommaSeparated(params.brands),
  meBrands: toCommaSeparated(params.meBrands),
  shopNames: toCommaSeparated(params.shopNames),
});

const buildAvgDiscountDepthByCategoryParams = (
  params: ProductHealthAvgDiscountDepthByCategoryQuery
) => ({
  ...params,
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  brands: toCommaSeparated(params.brands),
  shopNames: toCommaSeparated(params.shopNames),
});

const buildContentQualityParams = (
  params: ProductHealthContentQualityQuery
) => ({
  ...params,
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  brands: toCommaSeparated(params.brands),
  meBrands: toCommaSeparated(params.meBrands),
});

const buildShareOfSearchParams = (
  params: ShareOfSearchSearchInsightsQuery
) => ({
  ...params,
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  brands: toCommaSeparated(params.brands),
  keywords: toCommaSeparated(params.keywords),
});

const buildShareOfSearchKeywordOptionsParams = (
  params: ShareOfSearchKeywordOptionsQuery
) => ({
  ...params,
  startDate: toParamValue(params.startDate),
  endDate: toParamValue(params.endDate),
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
});

const buildCompetitorBrandsParams = (params: CompetitorBrandsQuery) => ({
  ...params,
  country: toCountryCode(params.country),
  countries: toCommaSeparated(toCountryCodes(params.countries)),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
});

const buildBrandOptionsParams = (params: BrandOptionsQuery) => ({
  ...params,
  country: toCountryCode(params.country),
  countries: toCommaSeparated(toCountryCodes(params.countries)),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
});

const buildBannerPresenceParams = (params: BannerPresenceQuery) => ({
  ...params,
  country: toCountryCode(params.country),
  channels: toCommaSeparated(params.channels),
  ...toCategoryRequestParams(params),
  brands: toCommaSeparated(params.brands),
  meBrands: toCommaSeparated(params.meBrands),
  knownBrands: toCommaSeparated(params.knownBrands),
});

@injectable()
export class DigitalShelfService {
  async getDigitalShelfSubscriptionDetails(): Promise<
    ApiResponse<DigitalShelfSubscriptionDetails>
  > {
    try {
      const response = await axiosClient.post<
        ApiResponse<DigitalShelfSubscriptionDetails>
      >(`/api/digital-shelf/subscription-details`, {});
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchKeywordOptions(
    params: ShareOfSearchKeywordOptionsQuery
  ): Promise<ApiResponse<ShareOfSearchKeywordOptionsResponse['data']>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<ShareOfSearchKeywordOptionsResponse['data']>
      >(
        `/api/digital-shelf/share-of-search/keyword-options`,
        buildShareOfSearchKeywordOptionsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getCompetitorBrands(
    params: CompetitorBrandsQuery
  ): Promise<ApiResponse<Array<{ id: number; name: string }>>> {
    // Deprecated Digital Shelf endpoint kept for rollback safety. Prefer the
    // shop-scoped `/api/digital-shelf/brand-options` flow for new callers.
    try {
      const response = await axiosClient.post<
        ApiResponse<Array<{ id: number; name: string }>>
      >(
        `/api/digital-shelf/competitor-brands`,
        buildCompetitorBrandsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getBrandOptions(
    params: BrandOptionsQuery
  ): Promise<ApiResponse<BrandOptionsResponse['data']>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<BrandOptionsResponse['data']>
      >(`/api/digital-shelf/brand-options`, buildBrandOptionsParams(params));
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getBannerPresenceOverview(
    params: BannerPresenceQuery
  ): Promise<ApiResponse<BannerPresenceOverviewResponse>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<BannerPresenceOverviewResponse>
      >(
        `/api/digital-shelf/banner-presence/overview`,
        buildBannerPresenceParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getBannerPresenceContentInsight(
    params: BannerPresenceQuery
  ): Promise<ApiResponse<BannerPresenceContentInsightResponse>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<BannerPresenceContentInsightResponse>
      >(
        `/api/digital-shelf/banner-presence/content-insight`,
        buildBannerPresenceParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getBannerPresenceGalleryLog(
    params: BannerPresenceQuery
  ): Promise<ApiResponse<BannerPresenceGalleryLogResponse>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<BannerPresenceGalleryLogResponse>
      >(
        `/api/digital-shelf/banner-presence/gallery-log`,
        buildBannerPresenceParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthMainMetrics(): Promise<ApiResponse<any>> {
    try {
      const response = await axiosClient.post<ApiResponse<any>>(
        `/api/digital-shelf/product-health/market`,
        {}
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthOnlineAvailabilityMetrics(
    params: ProductHealthOnlineAvailabilityQuery
  ): Promise<MetricsResponse> {
    try {
      const response = await axiosClient.post<MetricsResponse>(
        `/api/digital-shelf/product-health/online-availability/metrics`,
        buildOnlineAvailabilityParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthOnlineAvailabilityProducts(
    params: ProductHealthOnlineAvailabilityProductsQuery
  ): Promise<ApiResponse<ProductHealthOnlineAvailabilityProductsResult>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<ProductHealthOnlineAvailabilityProductsResult>
      >(
        `/api/digital-shelf/product-health/online-availability/products`,
        buildOnlineAvailabilityProductsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthRatingReviewsProducts(
    params: ProductHealthRatingReviewsProductsQuery
  ): Promise<ApiResponse<ProductHealthRatingsAndReviewsProductsResult>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<ProductHealthRatingsAndReviewsProductsResult>
      >(
        `/api/digital-shelf/product-health/ratings-reviews/products`,
        buildRatingReviewsProductsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthRatingReviewsMetrics(
    params: ProductHealthRatingReviewsQuery
  ): Promise<RatingReviewsMetricsResponse> {
    try {
      const response = await axiosClient.post<RatingReviewsMetricsResponse>(
        `/api/digital-shelf/product-health/ratings-reviews/metrics`,
        buildRatingReviewsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthPriceCompetitivenessMetrics(
    params: ProductHealthPriceCompetitivenessQuery
  ): Promise<PriceCompetitivenessMetricsResponse> {
    try {
      const response =
        await axiosClient.post<PriceCompetitivenessMetricsResponse>(
          `/api/digital-shelf/product-health/price-competitiveness/metrics`,
          buildPriceCompetitivenessParams(params)
        );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthAvgDiscountDepthTrend(
    params: ProductHealthAvgDiscountDepthTrendQuery
  ): Promise<AvgDiscountDepthTrendResponse> {
    try {
      const response = await axiosClient.post<AvgDiscountDepthTrendResponse>(
        `/api/digital-shelf/product-health/price-competitiveness/average-discount-depth-trend`,
        buildAvgDiscountDepthTrendParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthAvgDiscountDepthByCategory(
    params: ProductHealthAvgDiscountDepthByCategoryQuery
  ): Promise<AvgDiscountDepthByCategoryResponse> {
    try {
      const response =
        await axiosClient.post<AvgDiscountDepthByCategoryResponse>(
          `/api/digital-shelf/product-health/price-competitiveness/avg-discount-depth-by-category`,
          buildAvgDiscountDepthByCategoryParams(params)
        );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthContentQualityMetrics(
    params: ProductHealthContentQualityQuery
  ): Promise<ContentQualityMetricsResponse> {
    try {
      const response = await axiosClient.post<ContentQualityMetricsResponse>(
        `/api/digital-shelf/product-health/content-quality/metrics`,
        buildContentQualityParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthPriceCompetitivenessProducts(
    params: ProductHealthPriceCompetitivenessProductsQuery
  ): Promise<ApiResponse<ProductHealthPriceCompetitivenessProductsResult>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<ProductHealthPriceCompetitivenessProductsResult>
      >(
        `/api/digital-shelf/product-health/price-competitiveness/products`,
        buildPriceCompetitivenessProductsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthPriceCompetitivenessProductPriceTrend(
    params: ProductHealthSelectedProductPriceTrendQuery
  ): Promise<
    ApiResponse<ProductHealthSelectedProductPriceTrendResult['data']>
  > {
    try {
      const response = await axiosClient.post<
        ApiResponse<ProductHealthSelectedProductPriceTrendResult['data']>
      >(
        `/api/digital-shelf/product-health/price-competitiveness/product-price-trend`,
        buildSelectedProductPriceTrendParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  // ========================================
  // Price Tracking
  // ========================================

  async getPriceTrackingProducts(
    params: PriceTrackingProductsQuery
  ): Promise<ApiResponse<PriceTrackingProductsResult>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<PriceTrackingProductsResult>
      >(
        `/api/digital-shelf/price-tracking/products`,
        buildPriceTrackingProductsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getPriceTrackingProductPriceTrend(
    params: PriceTrackingTrendQuery
  ): Promise<ApiResponse<PriceTrackingTrendResult['data']>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<PriceTrackingTrendResult['data']>
      >(
        `/api/digital-shelf/price-tracking/product-price-trend`,
        buildPriceTrackingTrendParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getPriceTrackingProductFocus(
    params: PriceTrackingProductFocusQuery
  ): Promise<ApiResponse<PriceTrackingProductFocusResult['data']>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<PriceTrackingProductFocusResult['data']>
      >(
        `/api/digital-shelf/price-tracking/product-focus`,
        buildPriceTrackingProductFocusParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getPriceTrackingProductOptions(
    params: PriceTrackingProductOptionsQuery
  ): Promise<ApiResponse<PriceTrackingProductOptionsResult['data']>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<PriceTrackingProductOptionsResult['data']>
      >(
        `/api/digital-shelf/price-tracking/product-options`,
        buildPriceTrackingProductOptionsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getProductHealthContentQualityProducts(
    params: ProductHealthContentQualityProductsQuery
  ): Promise<ApiResponse<ProductHealthContentQualityProductsResult>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<ProductHealthContentQualityProductsResult>
      >(
        `/api/digital-shelf/product-health/content-quality/products`,
        buildContentQualityProductsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getGlobalScoreCardTableMetrics(
    params: GlobalScoreCardTableMetricsQuery
  ): Promise<ApiResponse<GlobalScoreCardTableMetricsData>> {
    try {
      const requestBody = {
        ...params,
        countries: toCountryCodes(params.countries),
        channels: params.channels ?? [],
        brands: params.brands ?? [],
        ...toCategoryRequestParams(params),
      };
      const response = await axiosClient.post<
        ApiResponse<GlobalScoreCardTableMetricsData>
      >(`/api/digital-shelf/global-score-card/table-metrics`, requestBody);
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getGlobalScoreCardCountryTableMetricsBatch(
    params: GlobalScoreCardCountryTableMetricsBatchQuery
  ): Promise<CountryTableMetricsBatchResponse> {
    try {
      const response = await axiosClient.post<CountryTableMetricsBatchResponse>(
        `/api/digital-shelf/global-score-card/country-table-metrics-batch`,
        {
          startDate: params.startDate,
          endDate: params.endDate,
          countries: toCountryCodes(params.countries),
          channels: params.channels ?? [],
          brands: params.brands ?? [],
          ...toCategoryRequestParams(params),
          globalBenchmark: params.globalBenchmark,
          countryBenchmarks: params.countryBenchmarks,
        }
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getBenchmarkConfig(
    countryCode: string
  ): Promise<GetBenchmarkConfigResponse> {
    try {
      const response = await axiosClient.get<GetBenchmarkConfigResponse>(
        `/api/digital-shelf/benchmark-config`,
        { params: { country: countryCode } }
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getDigitalShelfAlertSummary(
    module: DsAlertModule,
    startDate: string,
    country: string
  ): Promise<GetDsAlertSummaryResponse> {
    try {
      const response = await axiosClient.get<GetDsAlertSummaryResponse>(
        `/api/digital-shelf/alert-summary/${module}`,
        { params: { startDate, country } }
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getDigitalShelfAlerts(
    module: DsAlertModule,
    params: GetDsAlertsParams
  ): Promise<GetDsAlertsResponse> {
    try {
      const response = await axiosClient.get<GetDsAlertsResponse>(
        `/api/digital-shelf/alerts/${module}`,
        { params }
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async upsertBenchmarkConfig(params: {
    countryCode: string;
    moduleTiers: BenchmarkModuleTier[];
    rows: BenchmarkConfigRow[];
  }): Promise<UpsertBenchmarkConfigResponse> {
    try {
      const response = await axiosClient.put<UpsertBenchmarkConfigResponse>(
        `/api/digital-shelf/benchmark-config`,
        params
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getGlobalScoreCardTrendMetrics(
    params: GlobalScoreCardTrendMetricsQuery
  ): Promise<ApiResponse<GlobalScoreCardTrendMetricsData>> {
    try {
      const response = await axiosClient.post<
        ApiResponse<GlobalScoreCardTrendMetricsData>
      >(
        `/api/digital-shelf/global-score-card/trend-metrics`,
        buildGlobalScoreCardTrendMetricsParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchSearchInsightsMetrics(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<SearchInsightsMetricsResponse> {
    try {
      const response = await axiosClient.post<SearchInsightsMetricsResponse>(
        `/api/digital-shelf/share-of-search/search-insights/metrics`,
        buildShareOfSearchParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchBrandLeaderboard(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<BrandLeaderboardResponse> {
    try {
      const response = await axiosClient.post<BrandLeaderboardResponse>(
        `/api/digital-shelf/share-of-search/search-insights/brand-leaderboard`,
        buildShareOfSearchParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchTopPlayerRanking(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<SearchInsightsTopPlayerResponse> {
    try {
      const response = await axiosClient.post<SearchInsightsTopPlayerResponse>(
        `/api/digital-shelf/share-of-search/search-insights/top-player-ranking`,
        buildShareOfSearchParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchSearchInsightsRankingCards(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<SearchInsightsRankingCardsResponse> {
    try {
      const response =
        await axiosClient.post<SearchInsightsRankingCardsResponse>(
          `/api/digital-shelf/share-of-search/search-insights/ranking-cards`,
          buildShareOfSearchParams(params)
        );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchSearchInsightsHeadlineCards(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<SearchInsightsHeadlineCardsResponse> {
    try {
      const response =
        await axiosClient.post<SearchInsightsHeadlineCardsResponse>(
          `/api/digital-shelf/share-of-search/search-insights/headline-cards`,
          buildShareOfSearchParams(params)
        );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchCompetitiveGapTrend(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<CompetitiveGapTrendResponse> {
    try {
      const response = await axiosClient.post<CompetitiveGapTrendResponse>(
        `/api/digital-shelf/share-of-search/search-insights/competitive-gap-trend`,
        buildShareOfSearchParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchKeywordScatterPlot(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<KeywordInsightsScatterPlotResponse> {
    try {
      const response =
        await axiosClient.post<KeywordInsightsScatterPlotResponse>(
          `/api/digital-shelf/share-of-search/keyword-insights/scatter-plot`,
          buildShareOfSearchParams(params)
        );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchKeywordMetrics(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<KeywordInsightsMetricsResponse> {
    try {
      const response = await axiosClient.post<KeywordInsightsMetricsResponse>(
        `/api/digital-shelf/share-of-search/keyword-insights/metrics`,
        buildShareOfSearchParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchKeywordDistribution(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<KeywordInsightsDistributionResponse> {
    try {
      const response =
        await axiosClient.post<KeywordInsightsDistributionResponse>(
          `/api/digital-shelf/share-of-search/keyword-insights/keyword-distribution`,
          buildShareOfSearchParams(params)
        );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchKeywordPositionTable(params: {
    date: string;
    startDate: string;
    channels?: string[];
    country?: string;
    categoryPaths?: string[];
    brands?: string[];
    keywords?: string[];
    page?: number;
    pageSize?: number;
    searchText?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<KeywordInsightsPositionTableResponse>> {
    try {
      const requestParams = {
        ...params,
        country: toCountryCode(params.country),
        channels: toCommaSeparated(params.channels),
        categoryPaths: toCommaSeparated(params.categoryPaths),
        brands: toCommaSeparated(params.brands),
        keywords: toCommaSeparated(params.keywords),
      };
      const response = await axiosClient.post<
        ApiResponse<KeywordInsightsPositionTableResponse>
      >(
        `/api/digital-shelf/share-of-search/keyword-insights/position-table`,
        requestParams
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getShareOfSearchResellerOutranksOfficial(
    params: ShareOfSearchSearchInsightsQuery
  ): Promise<ResellerOutranksOfficialResponse> {
    try {
      const response = await axiosClient.post<ResellerOutranksOfficialResponse>(
        `/api/digital-shelf/share-of-search/keyword-insights/reseller-outranks-official`,
        buildShareOfSearchParams(params)
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403) {
        throw new Error(ERRORS.missingSubscription);
      }
      throw new Error(ERRORS.unexpectedError);
    }
  }
}
